// BudgetPage.jsx
import React, { useState, useMemo, useEffect, useCallback, useContext } from "react";
import T from '../../components/common/T';
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from '../../api/axiosConfig';

import { useProjects } from "../../context/ProjectContext";
import { useBudgets } from "../../context/BudgetContext";
import { useAuth } from "../../context/AuthContext";
import { CurrencyContext } from "../../context/CurrencyContext";
import { useTranslation } from "react-i18next";
import DataTable from "../../components/common/DataTables.jsx";
import TableActionButtons from "../../components/common/TableActionButtons";
import { TopPrimaryButton } from "../../components/common/StandardButtons";
import useModuleFilters from "../../hooks/useModuleFilters";
import PageLayout, { PageHeader, StatsGrid, Toolbar, FilterPanel, TableCard } from "../../components/common/PageLayout";

import SetBudgetModal from "./SetBudgetModal";
import EditBudgetModal from "./EditBudgetModal";

import {
  ArrowLeftIcon, ShieldCheckIcon, PencilSquareIcon, ChartBarIcon,
  CurrencyDollarIcon, ArrowTrendingUpIcon, ArrowTrendingDownIcon, FolderOpenIcon
} from "@heroicons/react/24/outline";

export default function BudgetPage() {
  const navigate = useNavigate();
  const { checkAccess, user } = useAuth();
  const { t } = useTranslation();
  const { displayMoney } = useContext(CurrencyContext);

  const { projects = [], loading: loadingProjects, fetchProjects } = useProjects() || {};
  const { budgets = [], fetchBudgets, loading: loadingBudgets } = useBudgets() || {};

  const [initialLoading, setInitialLoading] = useState(true);

  const loadAllData = useCallback(async () => {
    setInitialLoading(true);
    try {
      await Promise.allSettled([
        fetchBudgets ? fetchBudgets() : Promise.resolve(),
        fetchProjects ? fetchProjects() : Promise.resolve(),
      ]);
    } finally {
      setInitialLoading(false);
    }
  }, [fetchBudgets, fetchProjects]);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  const isLoading = initialLoading || Boolean(loadingProjects) || Boolean(loadingBudgets);

  const [viewMode, setViewMode] = useState("list");
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [financialData, setFinancialData] = useState(null);
  const [loadingFinancials, setLoadingFinancials] = useState(false);
  const [isSetModalOpen, setSetModalOpen] = useState(false);
  const [editingBudget, setEditingBudget] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const {
    showFilter,
    toggleFilters,
    filterOptions,
    loadingFilters,
  } = useModuleFilters("budget", {
    projects: [],
    statuses: [],
    fiscalYears: [],
  });

  const [statusFilter, setStatusFilter] = useState("All");
  const [sortFilter, setSortFilter] = useState("");

  const isSuperAdmin = user?.role === "Super Admin";

  const allProjectFinancials = useMemo(() => {
    if (!projects || projects.length === 0) return [];
    return projects
      .map((project) => {
        const dbBudget = budgets.find(b => String(b.projectId || b.project_id) === String(project.id));
        let plannedBudget = 0;
        if (dbBudget) {
          const totalFromDB = Number(dbBudget.plannedBudget) || Number(dbBudget.amount) || Number(dbBudget.projectBudget);
          plannedBudget = totalFromDB > 0 ? totalFromDB :
            Number(dbBudget.laborCostEstimate || 0) + Number(dbBudget.materialCostEstimate || 0) +
            Number(dbBudget.equipmentCostEstimate || 0) + Number(dbBudget.realCostEstimate || 0);
        } else {
          plannedBudget = Number(project.budget || 0);
        }
        return { ...project, dbBudget, plannedBudget };
      })
      .filter(row => row.name && (row.dbBudget || row.plannedBudget > 0));
  }, [projects, budgets]);

  const filteredProjectFinancials = useMemo(() => {
    let result = [...allProjectFinancials];
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      result = result.filter(p => p.name?.toLowerCase().includes(s) || p.client?.toLowerCase().includes(s));
    }
    if (statusFilter === "Active") {
      result = result.filter(p => p.dbBudget);
    } else if (statusFilter === "Estimate Only") {
      result = result.filter(p => !p.dbBudget);
    }
    if (sortFilter === "high-low") {
      result.sort((a, b) => (b.plannedBudget || 0) - (a.plannedBudget || 0));
    } else if (sortFilter === "low-high") {
      result.sort((a, b) => (a.plannedBudget || 0) - (b.plannedBudget || 0));
    }
    return result;
  }, [allProjectFinancials, searchTerm, statusFilter, sortFilter]);

  // ── Stats for list view ──────────────────────────────────────────────────
  const totalBudget = allProjectFinancials.reduce((s, r) => s + (r.plannedBudget || 0), 0);
  const activeCount = allProjectFinancials.filter(r => r.dbBudget).length;
  const estimateOnly = allProjectFinancials.filter(r => !r.dbBudget).length;

  const listStats = useMemo(() => [
    {
      label: "Total Projects",
      value: allProjectFinancials.length,
      icon: FolderOpenIcon,
      variant: "default",
      isActive: statusFilter === "All" || !statusFilter,
      onClick: () => setStatusFilter("All"),
    },
    {
      label: "Total Budget",
      value: displayMoney(totalBudget),
      icon: CurrencyDollarIcon,
      variant: "blue",
      valueClassName: "text-base sm:text-lg font-bold",
    },
    {
      label: "Active Budgets",
      value: activeCount,
      icon: ArrowTrendingUpIcon,
      variant: "green",
      isActive: statusFilter === "Active",
      onClick: activeCount > 0 ? () => setStatusFilter(s => s === "Active" ? "All" : "Active") : undefined,
    },
    {
      label: "Estimate Only",
      value: estimateOnly,
      icon: ArrowTrendingDownIcon,
      variant: "orange",
      isActive: statusFilter === "Estimate Only",
      onClick: estimateOnly > 0 ? () => setStatusFilter(s => s === "Estimate Only" ? "All" : "Estimate Only") : undefined,
    },
  ], [allProjectFinancials, totalBudget, activeCount, estimateOnly, displayMoney, statusFilter]);

  const fetchProjectFinancials = useCallback(async (pid) => {
    if (!pid) return;
    setLoadingFinancials(true);
    try {
      const res = await api.get(`/budgets/${pid}/financials`);
      const projectInfo = projects.find(p => String(p.id) === String(pid));
      const backendBudget = Number(res.data.plannedBudget || res.data.amount || res.data.projectBudget || 0);
      const finalBudget = backendBudget > 0 ? backendBudget : Number(projectInfo?.budget || 0);
      const totalSpent = Number(res.data.totalSpent || 0);
      const percentUsed = finalBudget > 0 ? (totalSpent / finalBudget) * 100 : 0;
      const variance = finalBudget - totalSpent;
      setFinancialData({
        ...res.data,
        plannedBudget: finalBudget, totalSpent, percentUsed, variance,
        name: projectInfo?.name || "Unknown Project",
        breakdown: res.data.breakdown || { poCost: 0, laborCost: 0, materialCost: 0 },
        revenue: res.data.revenue || { billed: 0 }
      });
    } catch {
      toast.error("Could not load financial details.");
    } finally {
      setLoadingFinancials(false);
    }
  }, [projects]);

  useEffect(() => {
    if (viewMode === 'detail' && selectedProjectId) fetchProjectFinancials(selectedProjectId);
  }, [viewMode, selectedProjectId, fetchProjectFinancials]);

  const handleViewDetails = (projectId) => { setSelectedProjectId(projectId); setViewMode("detail"); };

  const getProgressBarColor = (percent) => {
    if (percent >= 100) return "bg-red-500";
    if (percent >= 80) return "bg-amber-400";
    return "bg-emerald-500";
  };

  const projectsWithoutBudget = projects.filter(p =>
    p.name && !budgets.find(b => String(b.projectId || b.project_id) === String(p.id))
  );

  const columns = [
    {
      header: "Project", accessor: "name", sortable: true,
      render: (row) => (
        <div>
          <div className="font-bold text-gray-900 dark:text-white cursor-pointer hover:underline hover:text-indigo-600 dark:hover:text-indigo-400" onClick={() => handleViewDetails(row.id)}>{row.name}</div>
          <div className="text-[10px] text-gray-500 dark:text-slate-400">{row.client}</div>
        </div>
      )
    },
    {
      header: "Planned Budget", accessor: "plannedBudget", align: "right", sortable: true,
      render: (row) => <span className="font-mono text-gray-800 dark:text-slate-200 font-bold">{displayMoney(row.plannedBudget)}</span>
    },
    {
      header: "Status", accessor: "dbBudget", align: "center",
      render: (row) => (
        <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide border ${row.dbBudget ? 'bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800/60' : 'bg-gray-50 dark:bg-slate-800 text-gray-500 dark:text-slate-400 border-gray-200 dark:border-slate-700'}`}>
          {row.dbBudget ? "Active" : "Estimate Only"}
        </span>
      )
    },
    {
      header: "Actions", align: "right",
      render: (row) => (
        <TableActionButtons
          onView={(e) => { e.stopPropagation(); handleViewDetails(row.id); }}
          viewTitle="View Financials"
          onEdit={isSuperAdmin && row.dbBudget ? (e) => { e.stopPropagation(); setEditingBudget(row.dbBudget); } : undefined}
          editTitle="Edit Budget"
        />
      )
    }
  ];

  return (
    <>
      <div className="flex flex-col h-full">

        {/* VIEW 1: PROJECT LIST */}
        {viewMode === "list" && (
          <PageLayout>
            {/* Header */}
            <PageHeader
              icon={CurrencyDollarIcon}
              iconColor="text-[#E8612C]"
              title="Budget Overview"
              subtitle="Monitor project budgets, allocations, and expenditures in real-time"
              onRefresh={loadAllData}
              loading={isLoading}
            >
              {isSuperAdmin && projectsWithoutBudget.length > 0 && (
                <TopPrimaryButton
                  onClick={() => setSetModalOpen(true)}
                  label="Set Budget"
                />
              )}
            </PageHeader>

            {/* Stats mini dashboard */}
            <StatsGrid stats={listStats} cols={4} loading={isLoading} />

            {/* Unified Search & Filters Toolbar */}
            <Toolbar
              searchTerm={searchTerm}
              onSearch={setSearchTerm}
              searchPlaceholder="Search budgets by project..."
              tabs={['All', 'Active', 'Estimate Only']}
              activeTab={statusFilter}
              onTabChange={setStatusFilter}
              onToggleFilters={toggleFilters}
              showFilters={showFilter}
            />

            {/* Collapsible Filter Panel */}
            <FilterPanel
              isOpen={showFilter}
              loading={loadingFilters}
              onClear={() => { setSearchTerm(''); setStatusFilter('All'); setSortFilter(''); }}
            >
              <div>
                <label className="block text-xs text-gray-500 dark:text-slate-400 mb-1 flex items-center justify-between">
                  <span><T>Budget Status</T></span>
                  {loadingFilters && <span className="text-[10px] text-gray-400 animate-pulse font-normal">Loading...</span>}
                </label>
                <select
                  value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value)}
                  disabled={loadingFilters}
                  className="w-full border border-gray-300 dark:border-[#30363d] rounded-lg p-2 text-sm bg-white dark:bg-[#21262d] text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-[#E8612C]"
                >
                  <option value="All"><T>All Projects / Budgets</T></option>
                  {(filterOptions.statuses?.length > 0 ? filterOptions.statuses : ["Active", "Estimate Only", "Draft", "Archived"]).map(st => (
                    <option key={st} value={st}><T>{st}</T></option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 dark:text-slate-400 mb-1"><T>Sort by Budget</T></label>
                <select
                  value={sortFilter}
                  onChange={e => setSortFilter(e.target.value)}
                  className="w-full border border-gray-300 dark:border-[#30363d] rounded-lg p-2 text-sm bg-white dark:bg-[#21262d] text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-[#E8612C]"
                >
                  <option value=""><T>Default</T></option>
                  <option value="high-low"><T>Highest Budget First</T></option>
                  <option value="low-high"><T>Lowest Budget First</T></option>
                </select>
              </div>
            </FilterPanel>

            {/* Table Card */}
            <TableCard>
              <DataTable
                data={filteredProjectFinancials}
                columns={columns}
                loading={isLoading}
                loadingMessage="Loading budget and project data..."
                emptyMessage="No active budgets found."
              />
            </TableCard>
          </PageLayout>
        )}

        {/* VIEW 2: DETAILED DASHBOARD */}
        {viewMode === "detail" && (
          <div className="animate-fade-in-up p-6">
            <div className="flex justify-between items-end border-b border-gray-200 dark:border-[#30363d] pb-4 mb-6">
              <div className="flex items-center gap-4">
                <button onClick={() => setViewMode("list")} className="p-2 bg-white dark:bg-[#161b22] border border-gray-200 dark:border-[#30363d] rounded-lg hover:bg-gray-50 dark:hover:bg-[#21262d] text-gray-700 dark:text-slate-200 transition">
                  <ArrowLeftIcon className="w-4 h-4" />
                </button>
                <div>
                  <h1 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white">{financialData?.name || "Loading..."}</h1>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`text-[10px] font-bold px-2 py-0.5 uppercase tracking-wide rounded-full border ${financialData?.status === 'On Track' ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800/60' : 'bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800/60'}`}>
                      {financialData?.status || "..."}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {loadingFinancials || !financialData ? (
              <div className="p-16 text-center text-gray-400 dark:text-slate-500 flex flex-col items-center justify-center gap-3">
                <div className="w-8 h-8 border-3 border-[#E8612C] border-t-transparent rounded-full animate-spin" />
                <p className="text-xs font-semibold animate-pulse"><T>Loading live financial data...</T></p>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white dark:bg-[#161b22] p-5 rounded-xl shadow-sm border border-gray-200 dark:border-[#30363d]">
                    <p className="text-xs font-bold text-gray-500 dark:text-slate-400 uppercase"><T>Planned Budget</T></p>
                    <h3 className="text-base font-bold text-gray-900 dark:text-white mt-1">{displayMoney(financialData.plannedBudget)}</h3>
                  </div>
                  <div className="bg-white dark:bg-[#161b22] p-5 rounded-xl shadow-sm border border-gray-200 dark:border-[#30363d] border-l-4 border-l-indigo-500">
                    <p className="text-xs font-bold text-indigo-600 dark:text-indigo-400 uppercase"><T>Actual Spent</T></p>
                    <h3 className="text-base font-bold text-gray-900 dark:text-white mt-1">{displayMoney(financialData.totalSpent)}</h3>
                  </div>
                  <div className={`p-5 rounded-xl shadow-sm border ${financialData.variance < 0 ? 'bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-900/60' : 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-900/60'}`}>
                    <p className={`text-xs font-bold uppercase ${financialData.variance < 0 ? 'text-red-700 dark:text-red-400' : 'text-emerald-700 dark:text-emerald-400'}`}><T>Remaining</T></p>
                    <h3 className={`text-base font-bold mt-1 ${financialData.variance < 0 ? 'text-red-800 dark:text-red-300' : 'text-emerald-800 dark:text-emerald-300'}`}>{displayMoney(Math.abs(financialData.variance))}</h3>
                  </div>
                </div>

                <div className="bg-white dark:bg-[#161b22] p-6 rounded-xl shadow-sm border border-gray-200 dark:border-[#30363d]">
                  <div className="flex justify-between items-end mb-3">
                    <h3 className="font-bold text-base text-gray-800 dark:text-white uppercase tracking-wide"><T>Budget Utilization</T></h3>
                    <span className={`text-xl font-bold ${financialData.percentUsed > 100 ? 'text-red-600 dark:text-red-400' : 'text-gray-700 dark:text-slate-300'}`}>
                      {financialData.percentUsed.toFixed(1)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-100 dark:bg-slate-800 rounded-full h-3 overflow-hidden">
                    <div className={`h-3 rounded-full transition-all duration-1000 ${getProgressBarColor(financialData.percentUsed)}`} style={{ width: `${Math.min(financialData.percentUsed, 100)}%` }}></div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-white dark:bg-[#161b22] p-4 rounded-xl shadow-sm border border-gray-200 dark:border-[#30363d]">
                    <p className="text-[10px] font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider mb-1"><T>Purchase Orders</T></p>
                    <h3 className="text-base font-bold text-gray-800 dark:text-white">{displayMoney(financialData.breakdown.poCost)}</h3>
                  </div>
                  <div className="bg-white dark:bg-[#161b22] p-4 rounded-xl shadow-sm border border-gray-200 dark:border-[#30363d]">
                    <p className="text-[10px] font-bold text-purple-600 dark:text-purple-400 uppercase tracking-wider mb-1"><T>Labor</T></p>
                    <h3 className="text-base font-bold text-gray-800 dark:text-white">{displayMoney(financialData.breakdown.laborCost)}</h3>
                  </div>
                  <div className="bg-white dark:bg-[#161b22] p-4 rounded-xl shadow-sm border border-gray-200 dark:border-[#30363d]">
                    <p className="text-[10px] font-bold text-amber-600 dark:text-amber-400 uppercase tracking-wider mb-1"><T>Materials</T></p>
                    <h3 className="text-base font-bold text-gray-800 dark:text-white">{displayMoney(financialData.breakdown.materialCost)}</h3>
                  </div>
                  <div className="bg-white dark:bg-[#161b22] p-4 rounded-xl shadow-sm border border-gray-200 dark:border-[#30363d]">
                    <p className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider mb-1"><T>Invoiced Revenue</T></p>
                    <h3 className="text-base font-bold text-gray-800 dark:text-white">{displayMoney(financialData.revenue.billed)}</h3>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {isSetModalOpen && <SetBudgetModal isOpen={isSetModalOpen} onClose={() => setSetModalOpen(false)} availableProjects={projectsWithoutBudget} onSuccess={fetchBudgets} />}
        {editingBudget && <EditBudgetModal budget={editingBudget} onClose={() => setEditingBudget(null)} onSuccess={fetchBudgets} userId={user?.id} />}
      </div>
    </>
  );
}